import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { DroppedFile } from "../directives/global-file-drag-drop.directive";

export interface FileUploadConfig {
  uploadMethod: (files: DroppedFile[]) => void | Observable<unknown>;
  acceptedTypes?: string[];
  maxFileSize?: number; // in MB
  maxFiles?: number;
  showSuccessMessage?: boolean;
  showErrorMessage?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class GlobalFileUploadService {
  /**
   * Process dropped files using the provided configuration
   */
  processDroppedFiles(
    droppedFiles: DroppedFile[],
    config: FileUploadConfig
  ): void {
    try {
      // Call the upload method
      const result = config.uploadMethod(droppedFiles);

      // Handle Observable result
      if (result && typeof result.subscribe === "function") {
        result.subscribe({
          next: () => {
            if (config.showSuccessMessage) {
              this.showMessage("Files uploaded successfully!", "success");
            }
          },
          error: error => {
            if (config.showErrorMessage) {
              this.showMessage("Upload failed. Please try again.", "error");
            }
            console.error("File upload error:", error);
          },
        });
      } else if (config.showSuccessMessage) {
        this.showMessage("Files processed successfully!", "success");
      }
    } catch (error) {
      if (config.showErrorMessage) {
        this.showMessage("Upload failed. Please try again.", "error");
      }
      console.error("File upload error:", error);
    }
  }

  /**
   * Handle rejected files
   */
  handleRejectedFiles(rejectedFiles: { file: File; reason: string }[]): void {
    const reasons = rejectedFiles.map(rf => rf.reason).join(", ");
    this.showMessage(`Some files were rejected: ${reasons}`, "warning");
  }

  /**
   * Convert File to base64 string
   */
  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1]); // Remove data:type;base64, prefix
      };
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Get file extension
   */
  getFileExtension(fileName: string): string {
    return fileName.split(".").pop()?.toLowerCase() ?? "";
  }

  /**
   * Format file size
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  /**
   * Show message (you can customize this based on your notification system)
   */
  private showMessage(
    message: string,
    type: "success" | "error" | "warning"
  ): void {
    // Replace this with your app's notification system
    // For example: this.toastr.success(message), this.messageService.add(), etc.

    // Simple alert for now - replace with your preferred notification
    if (type === "error") {
      alert(`Error: ${message}`);
    }
  }
}
